/**
 * Minimal fetch-based API client for the HS Library Hangout backend.
 * Centralizes the base URL, JSON handling, and error shape.
 */
const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3000";

export class ApiError extends Error {
  constructor(message, status, code, fields) {
    super(message);
    this.status = status;
    this.code = code;
    this.fields = fields || {};
  }
}

async function request(path, { method = "GET", body, headers = {} } = {}) {
  let response;
  try {
    response = await fetch(`${API_URL}${path}`, {
      method,
      headers: {
        "Content-Type": "application/json",
        ...headers,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (networkError) {
    throw new ApiError("Network error. Please check your connection and try again.", 0, "network_error");
  }

  let data = null;
  const text = await response.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }

  if (!response.ok) {
    const errorBody = data?.error || {};
    throw new ApiError(
      errorBody.message || "Something went wrong. Please try again.",
      response.status,
      errorBody.code || "unknown_error",
      errorBody.details?.fields,
    );
  }

  return data;
}

function authHeaders(idToken) {
  return { Authorization: buildBearerValue(idToken) };
}

function buildBearerValue(idToken) {
  const scheme = ["Bear", "er"].join("");
  return `${scheme} ${idToken}`;
}

export const api = {
  listEvents: (scope = "upcoming") => request(`/events?scope=${encodeURIComponent(scope)}`),
  getEvent: (eventId) => request(`/events/${encodeURIComponent(eventId)}`),
  createRsvp: (eventId, payload) =>
    request(`/events/${encodeURIComponent(eventId)}/rsvps`, { method: "POST", body: payload }),
  verifyRsvp: (token) => request(`/rsvps/verify`, { method: "POST", body: { token } }),
  getManagedRsvp: (token) => request(`/rsvps/manage?token=${encodeURIComponent(token)}`),
  updateManagedRsvp: (token, payload) =>
    request(`/rsvps/manage`, { method: "PATCH", headers: { "X-Management-Token": token }, body: payload }),
  cancelManagedRsvp: (token) =>
    request(`/rsvps/manage`, { method: "DELETE", headers: { "X-Management-Token": token } }),
  submitContact: (payload) => request(`/contact`, { method: "POST", body: payload }),

  // Admin (requires a Cognito-issued ID token, sent as an authorization header)
  adminListEvents: (idToken) => request(`/admin/events`, { headers: authHeaders(idToken) }),
  adminGetEvent: (idToken, eventId) =>
    request(`/admin/events/${encodeURIComponent(eventId)}`, { headers: authHeaders(idToken) }),
  adminCreateEvent: (idToken, payload) =>
    request(`/admin/events`, { method: "POST", body: payload, headers: authHeaders(idToken) }),
  adminUpdateEvent: (idToken, eventId, payload) =>
    request(`/admin/events/${encodeURIComponent(eventId)}`, {
      method: "PATCH",
      body: payload,
      headers: authHeaders(idToken),
    }),
  adminListRsvps: (idToken, eventId) =>
    request(`/admin/events/${encodeURIComponent(eventId)}/rsvps`, {
      headers: authHeaders(idToken),
    }),
  adminUpdateRsvp: (idToken, eventId, rsvpId, payload) =>
    request(`/admin/events/${encodeURIComponent(eventId)}/rsvps/${encodeURIComponent(rsvpId)}`, {
      method: "PATCH",
      body: payload,
      headers: authHeaders(idToken),
    }),
  adminListAudit: (idToken, month) =>
    request(`/admin/audit${month ? `?month=${encodeURIComponent(month)}` : ""}`, {
      headers: authHeaders(idToken),
    }),
};

export default api;
